import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';
import { useCloseWatcher } from '../hooks/useCloseWatcher';

export interface ContextMenuItem {
  key: string;
  label: string;
  // ElementType: lucide 아이콘의 propTypes(size: string|number)가 TS5에서
  // ComponentType<{size?: number}>와 비호환 판정되는 오탐 회피
  icon?: React.ElementType;
  danger?: boolean;
  /**
   * 라디오형 항목(현재 선택 표시). 정의하면 role이 menuitemradio로 바뀌고 오른쪽에 체크가 붙는다.
   * ⚠ 구분선 항목을 추가하지 말 것 — 화살표 키 이동이 items 배열 인덱스 기준이라 비항목이
   *   섞이면 포커스가 빈 줄에 멈춘다. 그룹 구분은 아이콘 모양으로 낸다.
   */
  checked?: boolean;
  onSelect: () => void;
}

interface ContextMenuProps {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
  header?: React.ReactNode;
  variant?: 'dark' | 'light';
  // 카카오톡식 모달 동작: 화면 전체를 덮어 스크롤을 차단하고,
  // 바깥 터치는 메뉴 닫기로만 소비됨 (아래 요소로 전달 안 됨)
  backdrop?: boolean;
}

// 백드롭 닫기 후 같은 제스처의 ghost click이 아래 요소(리액션 배지 등)로 관통하는 것을 차단
const suppressNextClick = () => {
  const swallow = (e: MouseEvent) => { e.preventDefault(); e.stopPropagation(); };
  document.addEventListener('click', swallow, { capture: true, once: true });
  // click이 오지 않는 경로(드래그 이탈 등) 대비 자동 해제
  setTimeout(() => document.removeEventListener('click', swallow, { capture: true }), 500);
};

// in-tree 렌더가 아니라 포털인 이유: 호출부(사이드바 래퍼 등)에 transform이 걸려 있어
// 그 안에서는 배치 기준이 깨진다. 포털 대상이 body가 아니라 #root인 이유: body의
// position:fixed는 iOS 키보드 패닝 중 기준 좌표계가 visual viewport와 어긋나
// 메뉴가 헤더 쪽으로 끌려 올라간다(예약 전송 롱프레스 사고 — 앵커·클램프를 고쳐도
// fixed 기준 자체가 어긋나면 소용없다). useSoftKeyboardViewportFix가 transform으로
// 보이는 영역에 맞춰 옮기는 #root 안에 absolute로 두면(KeyboardViewportDebug가 같은
// 원리로 실기기 검증됨) 키보드 유무·패닝 표현 방식(offsetTop/문서 스크롤)과 무관하게
// 항상 앵커 곁에 그려진다. x/y props는 client 좌표로 받아 내부에서 #root 상대로 변환한다.
const portalHost = () => document.getElementById('root') ?? document.body;

// 초기 포커스와 화살표 키 이동이 찾는 대상. ⚠ menuitemradio(checked를 준 항목)를 빼면
// 그 항목들이 키보드 이동에서 통째로 건너뛰어진다 — 상태 선택이 정확히 그 경우다.
const MENU_ITEM_SELECTOR = '[role="menuitem"],[role="menuitemradio"]';

// client 좌표 → 포털 호스트(#root) 상대 좌표. 두 gBCR 모두 같은 client 좌표계라
// 차이는 키보드 패닝·transform·스크롤 퀴크와 무관하게 항상 정확하다.
const toLocal = (host: HTMLElement, x: number, y: number) => {
  const r = host.getBoundingClientRect();
  return { x: x - r.left, y: y - r.top };
};

export const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, items, onClose, header, variant = 'dark', backdrop = false }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [host] = useState(portalHost);
  const [pos, setPos] = useState(() => toLocal(host, x, y));
  const isDark = variant !== 'light';
  // long-press로 여는 제스처의 ghost click(선행 pointerdown 없음)을 메뉴 항목에서 차단.
  // 실제 새 탭/클릭은 pointerdown이 먼저 오므로 arm된 후 통과된다.
  const armedRef = useRef(false);

  // 모바일 뒤로가기는 메뉴만 닫는다(화면은 그대로 — 안드로이드 표준)
  useCloseWatcher(true, onClose);

  // 호스트(#root) 박스 밖으로 넘치지 않게 클램핑. 키보드가 떠 있으면
  // useSoftKeyboardViewportFix가 #root height를 vv.height로 줄여놓으므로 이 클램프가
  // 곧 "보이는 영역" 클램프다. ⚠ window.innerHeight·visualViewport 수치를 직접 읽지
  // 말 것 — iOS 키보드 패닝 중 그 좌표계가 배치 기준과 어긋난 전력이 있다(예약 전송
  // 메뉴가 헤더 쪽에 뜨던 사고). 호스트와의 gBCR 차분만 신뢰한다.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const { width, height } = menu.getBoundingClientRect();
    const local = toLocal(host, x, y);
    setPos({
      x: Math.max(4, Math.min(local.x, host.clientWidth - width - 8)),
      y: Math.max(4, Math.min(local.y, host.clientHeight - height - 8)),
    });
  }, [x, y, host]);

  // 키보드로 열었을 때(Shift+F10·메뉴 키·버튼 Enter) 첫 항목에 포커스, 닫히면 원래 자리로 복원.
  // 터치/마우스로 열었을 땐 포커스를 옮기지 않는다 — 모바일 키보드가 뜨거나 스크롤이 튀는 것을 막기 위해
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    restoreFocusRef.current = prev;
    const usedKeyboard = !!prev && prev !== document.body && !('ontouchstart' in window && matchMedia('(pointer: coarse)').matches);
    if (usedKeyboard) {
      const first = menuRef.current?.querySelector<HTMLButtonElement>(MENU_ITEM_SELECTOR);
      // ⚠ preventScroll 필수. 이 effect는 위 useLayoutEffect의 클램프 setPos가 커밋되기
      // 전에 돈다(sync 렌더의 passive effect는 커밋 직후 즉시 flush). 그 순간 메뉴는 아직
      // 앵커 원좌표(예: rail 하단 아바타 → 화면 아래로 넘침)에 있고, #root 포털은 fixed가
      // 아니라 absolute라 넘친 만큼 문서 스크롤 범위가 생긴다. 여기서 focus()가 그 범위로
      // 스크롤을 일으키면 아래 window 'scroll' 캡처 리스너가 받아 onClose() → 메뉴가 떴다가
      // 즉시 사라진다(마우스로 <button>을 눌러 열면 activeElement가 그 버튼이라 이 분기를
      // 탄다 — 아바타 팝오버가 정확히 이 경로였다. body+fixed 포털 시절엔 넘쳐도 스크롤
      // 범위가 안 생겨 드러나지 않았다)
      first?.focus({ preventScroll: true });
    }
    return () => {
      const el = restoreFocusRef.current;
      if (usedKeyboard && el && document.contains(el)) el.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      // preventDefault: 처리한 Esc가 닫기 요청(CloseWatcher)으로 번지면 화면 단계 watcher까지 닫힌다
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      const menu = menuRef.current;
      if (!menu) return;
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
      const items = Array.from(menu.querySelectorAll<HTMLButtonElement>(MENU_ITEM_SELECTOR));
      if (items.length === 0) return;
      e.preventDefault();
      const idx = items.indexOf(document.activeElement as HTMLButtonElement);
      let next = 0;
      if (e.key === 'ArrowDown') next = idx < 0 ? 0 : (idx + 1) % items.length;
      else if (e.key === 'ArrowUp') next = idx < 0 ? items.length - 1 : (idx - 1 + items.length) % items.length;
      else if (e.key === 'Home') next = 0;
      else next = items.length - 1;
      items[next].focus();
    };
    const handleTouchStart = (e: TouchEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    // capture 단계: 메시지 리스트 등 내부 스크롤 컨테이너의 scroll도 감지 (window scroll은 버블 안 됨)
    const handleScroll = (e: Event) => {
      if (menuRef.current && e.target instanceof Node && menuRef.current.contains(e.target)) return;
      onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', onClose);
    window.addEventListener('blur', onClose);
    // backdrop 모드에선 바깥 상호작용을 백드롭이 전담하므로 window 감지는 비활성
    // (스크롤 자체가 차단되고, 자동 스크롤 등으로 메뉴가 튀듯 닫히는 것도 방지)
    if (!backdrop) {
      window.addEventListener('mousedown', handleMouseDown);
      window.addEventListener('touchstart', handleTouchStart);
      window.addEventListener('scroll', handleScroll, true);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('blur', onClose);
      if (!backdrop) {
        window.removeEventListener('mousedown', handleMouseDown);
        window.removeEventListener('touchstart', handleTouchStart);
        window.removeEventListener('scroll', handleScroll, true);
      }
    };
  }, [onClose, backdrop]);

  return createPortal(
    <>
    {backdrop && (
      <div
        className="absolute inset-0 z-40"
        // touch-action: none — 백드롭에서 시작한 터치로는 스크롤 불가 (메뉴만 닫힘)
        style={{ touchAction: 'none' }}
        // pointerdown은 새 제스처에서만 발생 — 메뉴를 연 제스처의 compat mouse 꼬리
        // (ghost mousedown/click)에는 면역. 닫은 뒤 click 관통도 차단.
        onPointerDown={() => {
          suppressNextClick();
          onClose();
        }}
      />
    )}
    <div
      ref={menuRef}
      role="menu"
      // select-none + touch-callout 억제: 롱프레스로 열면 손가락이 눌린 채 메뉴가 그 아래
      // 나타나는데, iOS가 이어지는 홀드를 라벨 텍스트 선택으로 해석한다('예약 전송' 선택 사고)
      className={`absolute z-50 rounded-md shadow-lg py-1 min-w-[160px] border select-none ${
        isDark ? 'bg-chrome-line border-slate-700' : 'bg-white border-slate-200'
      }`}
      style={{ left: pos.x, top: pos.y, WebkitTouchCallout: 'none' }}
      onContextMenu={e => e.preventDefault()}
      onPointerDownCapture={() => { armedRef.current = true; }}
      onKeyDownCapture={() => { armedRef.current = true; }}
      onClickCapture={(e) => {
        // 선행 pointerdown 없는 click = 여는 제스처의 ghost → 차단
        // (주의: mousedown으로 arm하면 ghost mousedown이 가드를 무력화하므로 금지)
        if (!armedRef.current) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      {header && (
        <div className={`px-2 pt-1.5 pb-1 mb-1 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          {header}
        </div>
      )}
      {items.map(item => {
        const Icon = item.icon;
        return (
          <button
            key={item.key}
            type="button"
            {...(item.checked === undefined
              ? { role: 'menuitem' }
              : { role: 'menuitemradio', 'aria-checked': item.checked })}
            onClick={item.onSelect}
            className={`w-full px-3 py-2.5 min-h-[40px] text-sm text-left flex items-center gap-2.5 transition-colors focus:outline-none focus-visible:bg-slate-100 ${
              isDark ? 'hover:bg-chrome' : 'hover:bg-slate-100'
            } ${
              item.danger
                ? (isDark ? 'text-brand-400 hover:text-brand-300' : 'text-brand-600 hover:text-brand-700')
                : (isDark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900')
            }`}
          >
            {Icon && <Icon size={14} className="shrink-0" />}
            <span className="flex-1">{item.label}</span>
            {item.checked && <Check size={14} className="shrink-0" aria-hidden="true" />}
          </button>
        );
      })}
    </div>
    </>,
    host
  );
};
